// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  ICertificationProcessGetAllAttrs,
  ICertificationProcessGetAttrs,
  ICertificationProcessSaveAttrs,
} from 'interfaces/back-office/content-management/certification/process/certification-process-view.interface'
import {
  ICertificationProcessApiGetAllResponse,
  ICertificationProcessApiGetResponse,
  ICertificationProcessApiPaginatedResponse,
  ICertificationProcessApiSaveResponse,
} from 'interfaces/back-office/content-management/certification/process/certification-process-api.interface'
import { ICertificationProcessDetail } from 'interfaces/back-office/content-management/certification/process'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Certification Process Props
const certificationProcessProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Certification Process List Model
const CertificationProcesses = types
  .model('CertificationProcesses')
  .props(withHelper_baseResponsePaginationTypes(types.model('CertificationProcedureList').props(certificationProcessProps)))

// Certification Process Detail Model
const CertificationProcessDetail = types.model('CertificationProcessDetail').props(omit(certificationProcessProps, ['number']))

const CertificationProcessStoreModel = types
  .model('CertificationProcess')
  .props({
    certificationProcess_isLoading: types.optional(types.boolean, false),
    certificationProcess_isSaveLoading: types.optional(types.boolean, false),
    certificationProcess_list: types.optional(CertificationProcesses, {}),
    certificationProcess_detail: types.optional(CertificationProcessDetail, {}),
  })
  .actions(self => ({
    certificationProcess_SET_LOADING: (payload: boolean): void => {
      self.certificationProcess_isLoading = payload
    },
    certificationProcess_SET_SAVE_LOADING: (payload: boolean): void => {
      self.certificationProcess_isSaveLoading = payload
    },
    certificationProcess_SET_LIST: (payload: ICertificationProcessApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.certificationProcess_list = cast(mapPayload)
    },
    certificationProcess_SET_DETAIL: (payload: ICertificationProcessDetail): void => {
      self.certificationProcess_detail = cast(payload)
    },
    certificationProcess_CLEAR_LIST: (): void => {
      self.certificationProcess_list = cast({})
    },
    certificationProcess_CLEAR_DETAIL: (): void => {
      self.certificationProcess_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch List
     *
     * @param {ICertificationProcessGetAllAttrs} payload
     *
     * @return {any} any
     */
    certificationProcess_fetchList: flow(function* (payload: ICertificationProcessGetAllAttrs) {
      self.certificationProcess_SET_LOADING(true)

      const response: ICertificationProcessApiGetAllResponse =
        yield self.environment.certificationProcessApi.certificationProcess_getList({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) self.certificationProcess_SET_LIST(response.res.data)

      self.certificationProcess_SET_LOADING(false)
    }),

    /**
     * @description Fetch certification process
     *
     * @param {ICertificationProcessGetAttrs} payload
     *
     * @return {any} any
     */
    certificationProcess_fetch: flow(function* (payload: ICertificationProcessGetAttrs) {
      self.certificationProcess_SET_LOADING(true)

      const response: ICertificationProcessApiGetResponse =
        yield self.environment.certificationProcessApi.certificationProcess_getDetail({
          params: { id: payload.params.id },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.certificationProcess_SET_DETAIL(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.certificationProcess_SET_LOADING(false)
    }),

    /**
     * @description Save certification process
     *
     * @param {ICertificationProcessSaveAttrs} payload
     *
     * @return {any} any
     */
    certificationProcess_save: flow(function* (payload: ICertificationProcessSaveAttrs) {
      self.certificationProcess_SET_SAVE_LOADING(true)

      const response: ICertificationProcessApiSaveResponse =
        yield self.environment.certificationProcessApi.certificationProcess_save({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.certificationProcess_SET_SAVE_LOADING(false)
    }),
  }))

export const createCertificationProcessStoreDefaultModel = () => types.optional(CertificationProcessStoreModel, {})
